import React from 'react';
import PropTypes from 'prop-types';
import Link from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Popover from '@material-ui/core/Popover';
import { useTranslation } from 'q3-ui-locale';
import axios from 'axios';
import moment from 'moment';
import { AuthContext } from 'q3-ui-permissions';
import SystemUpdateAltIcon from '@material-ui/icons/SystemUpdateAlt';
// eslint-disable-next-line
import { useOpen } from 'useful-state';
import GetAppIcon from '@material-ui/icons/GetApp';
import useXls from '../useExcel';
import { getCurrency, getPaymentOption } from './utils';

export const getDiff = (
  firstBatch,
  total,
  limitingValue,
) => {
  const diff = total - firstBatch;
  return diff !== 0
    ? Math.ceil(diff / Math.max(firstBatch, limitingValue))
    : 0;
};

export const getBulk = async (
  query,
  limitingValue = 500,
) => {
  const replaceLimit = () =>
    typeof query === 'string'
      ? query.replace('limit=16', `limit=${limitingValue}`)
      : `limit=${limitingValue}`;

  const getEndpoint =
    () =>
    (offset = -1) => {
      const q = `${replaceLimit(
        query,
        limitingValue,
      )}&fields=components,sku,manufacturerSku,excerpt,price,stock,inventory,vendor,currencyDefault&page=${
        offset + 1
      }`;

      return ['/products', q].join(
        q.startsWith('?') ? '' : '?',
      );
    };

  const getTotal = (res) => res?.data?.total || 0;
  const getProducts = (res) => res?.data?.products || [];

  const offset = getEndpoint(query, limitingValue);
  const response = await axios.get(offset());
  const products = getProducts(response);

  const length = getDiff(
    products.length,
    getTotal(response),
    limitingValue,
  );

  await Array.from({ length }).reduce(
    async (previousPromise, next, i) => {
      await previousPromise;
      return axios
        .get(offset(i))
        .then((res) => products.push(getProducts(res)));
    },
    Promise.resolve(),
  );

  return products.flat();
};

const hasPricing = (a = []) =>
  a.some((item) => item?.price?.custom);

const hasStock = (a = []) =>
  a.some((item) => item?.inventory?.hasAvailable);

export const mapToPricingExport = (a = [], t) => {
  const includePricingColumns = hasPricing(a);
  const includeStock = hasStock(a);

  return a.map((item) => ({
    [t('sku')]: item.sku,
    [t('manufacturerSku')]: item.manufacturerSku,
    ...(includePricingColumns
      ? {
          [t('price')]: item?.price?.custom,
          [t('msrp')]: item?.price?.msrp,
        }
      : {}),
    ...(includeStock
      ? {
          [t('inventory')]:
            item?.inventory?.hasAvailable || 0,
          [t('inventoryMessage')]:
            item?.stock?.message || '',
        }
      : {}),

    [t('vendor')]: item?.vendor?.name,
    [t('excerpt')]: item.excerpt,
  }));
};

const ProductExportButton = ({ children, query }) => {
  const [loading, setLoading] = React.useState();
  const { t } = useTranslation('labels');
  const name =
    React.useContext(AuthContext)?.state?.profile?.customer
      ?.name;

  const download = async () => {
    setLoading(true);
    return getBulk(query)
      .then((data) => {
        useXls(mapToPricingExport(data, t), {
          title: name
            ? `(${moment().format(
                'YYYY-MM-DD',
              )}) Pricelist for ${name} based on preferred payment method of ${getPaymentOption()} in ${getCurrency()}`
            : null,
        });
      })
      .catch(() => {
        // eslint-disable-next-line
        alert('Could not generate the file.');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      {/* eslint-disable-next-line */}
      <Link
        component="button"
        disabled={loading}
        onClick={download}
        style={{
          fontWeight: 'bold',
        }}
      >
        <SystemUpdateAltIcon
          style={{ transform: 'rotate(-90deg)' }}
        />
        {loading ? 'Please wait ...' : children}
      </Link>
    </Box>
  );
};

const ProductSearchExport = (props) => {
  const openState = useOpen();

  return (
    <>
      <Tooltip title="Export options">
        <IconButton
          color="primary"
          onClick={openState.open}
        >
          <GetAppIcon />
        </IconButton>
      </Tooltip>
      <Popover
        anchorEl={openState.anchorEl}
        open={openState.isOpen}
        onClose={openState.close}
      >
        <Box p={2} maxWidth={320}>
          <Typography>
            Generate a downloadable of our products, pricing
            and current availability. Please allow up to 60
            seconds for the full catalogue export to
            complete.
          </Typography>
          <Grid container spacing={1}>
            {!['?', 'undefined'].includes(props?.query) && (
              <Grid item xs={12}>
                <ProductExportButton {...props}>
                  Export last search/filter
                </ProductExportButton>
              </Grid>
            )}
            <Grid item xs={12}>
              <ProductExportButton>
                Export full catalogue
              </ProductExportButton>
            </Grid>
          </Grid>
        </Box>
      </Popover>
    </>
  );
};

ProductExportButton.propTypes = {
  children: PropTypes.string.isRequired,
  query: PropTypes.string.isRequired,
};

export default ProductSearchExport;
